import type { DrillDomainsResponse } from '~api/services';
import { getHardnessColour } from '~config';
import type { Point } from '~types';
import { polygonCenterToSVG } from '~utils';
import { Label, Polygon, useSvgContext } from '../components';

type DrillDomainsLayerProps = {
  /** Drill domains GeoJSON feature collection */
  domains: DrillDomainsResponse;
};

/**
 * Renders drill domains as colored polygons based on hardness classification.
 *
 * @example
 * ```tsx
 * <DrillDomainsLayer domains={drillDomainsData} />
 * ```
 */
const DrillDomainsLayer = ({ domains }: DrillDomainsLayerProps) => {
  const { transformer } = useSvgContext();

  if (!transformer || !domains?.features?.length) return null;

  return (
    <>
      {domains.features.map((feature, index) => {
        const { hardness, blast } = feature.properties;
        const colour = getHardnessColour(hardness);

        const coordinates = feature.geometry.coordinates[0];
        if (!coordinates?.length) return null;

        const coords: Point[] = coordinates.map((pos) => [pos[0], pos[1]] as Point);
        const center = polygonCenterToSVG(coords, transformer);
        if (!center) return null;

        return (
          <g
            className={`drill-domains drill-domains--${(hardness ?? 'unknown').toLowerCase()}`}
            // biome-ignore lint/suspicious/noArrayIndexKey: the features in a specific blast will not change
            key={`drill-${blast}-domain-${index}`}
          >
            <Polygon
              title={`Drill domain - ${colour.label || hardness}`}
              className={`drill-domain drill-domain--${(hardness ?? 'unknown').toLowerCase()}`}
              geometry={feature.geometry}
              fill={colour.fill}
              fillOpacity={colour.opacity}
              stroke={colour.stroke}
              strokeWidth={1}
              strokeOpacity={0.8}
            />
            <Label x={center.x} y={center.y} text={colour.label} fontSize={4} fontWeight={600} fill={colour.stroke} />
          </g>
        );
      })}
    </>
  );
};
DrillDomainsLayer.displayName = 'DrillDomainsLayer';

export { DrillDomainsLayer, type DrillDomainsLayerProps };
